import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";
import { generateTask as generatePrismTotalSurfaceTask } from "./TotalSurfaceTask";

const tex = String.raw;

// --- pomocnicze ---

function randomFrom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

function extractRawValue(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    if (v.raw != null) return String(v.raw);
    if (v.value != null) return String(v.value);
    if (v.parsed != null) return String(v.parsed);
  }
  return String(v);
}


// Bardzo prosty parser wyrażenia z pierwiastkiem (do SqrtInput)
function evaluateExpression(expr) {
  if (expr == null) return NaN;

  let cleaned = String(expr)
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/,/g, ".");

  if (!cleaned) return NaN;

  cleaned = cleaned
    .replace(/\\\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/sqrt\(([^)]+)\)/g, "sqrt($1)")
    .replace(/√/g, "sqrt");

  cleaned = cleaned
    .replace(/(\d)(sqrt)/g, "$1*sqrt")
    .replace(/(sqrt\([^)]*\))(\d)/g, "$1*$2");

  cleaned = cleaned.replace(/sqrt\(/g, "Math.sqrt(");

  if (!/^[0-9+\-*/().A-Za-z]*$/.test(cleaned)) {
    return NaN;
  }

  try {
    // eslint-disable-next-line no-new-func
    const f = new Function('"use strict"; return (' + cleaned + ");");
    const val = f();
    return typeof val === "number" && isFinite(val) ? val : NaN;
  } catch {
    return NaN;
  }
}

// --- GENERATOR ZADANIA ODWROTNEGO ---
// Dane: P_c (dokładne), pozostałe wymiary
// Szukane: h albo a (tam, gdzie równanie jest liniowe)

function generateReversePrismTotalSurfaceTask() {
  while (true) {
    const baseWrapper = generatePrismTotalSurfaceTask();
    const base = baseWrapper.data || baseWrapper;

    const { variant, isCube, a, h } = base;
    if (!variant && !isCube) continue;
    if (isCube && (!a || !isFinite(a) || a <= 0)) continue;
    if (!isCube && (!h || !isFinite(h) || h <= 0)) continue;

    // które przypadki dopuszczamy do szukania "a" (równanie liniowe)
    const linearATarget =
      variant === "triangle_scalene" ||
      variant === "rectangle" ||
      variant === "parallelogram" ||
      variant === "rhombus" ||
      variant === "trapezoid";

    let possibleTargets = [];

    if (isCube || variant === "cube") {
      possibleTargets = ["a"];
    } else if (variant === "triangle_right") {
      possibleTargets = ["h"]; // inaczej pojawia się nieliniowość przez Pitagorasa
    } else if (
      variant === "triangle_equilateral" ||
      variant === "square" ||
      variant === "regular_hexagon"
    ) {
      possibleTargets = ["h"]; // unikamy równań kwadratowych na a
    } else if (linearATarget) {
      possibleTargets = ["h", "a"];
    } else {
      possibleTargets = ["h"];
    }

    const target = randomFrom(possibleTargets);
    const targetSymbol = target;

    return {
      type: "ReverseTotalSurfaceTask",
      data: {
        ...base,
        target,
        targetSymbol,
      },
    };
  }
}

export function generateTask() {
  return generateReversePrismTotalSurfaceTask();
}

// --- TREŚĆ ZADANIA ---

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
    variant,
    isCube,
    baseName,
    a,
    b,
    c,
    d,
    baseHeight,
    aDisplay,
    bDisplay,
    cDisplay,
    dDisplay,
    baseHeightDisplay,
    hDisplay,
    target,
    totalAreaExpr,
    totalAreaValue,
  } = task;

  const aLatex = aDisplay || (a != null ? String(a) : "");
  const bLatex = bDisplay || (b != null ? String(b) : "");
  const cLatex = cDisplay || (c != null ? String(c) : "");
  const dLatex = dDisplay || (d != null ? String(d) : "");
  const haLatex = baseHeightDisplay || (baseHeight != null ? String(baseHeight) : "");
  const hLatex = hDisplay || (task.h != null ? String(task.h) : "");
  const pcExpr = totalAreaExpr || String(totalAreaValue);
  const pcLatex = `P_c = ${pcExpr}\\,\\text{cm}^2`;

  // SZEŚCIAN
  if (isCube || variant === "cube") {
    return (
      <p>
        Powierzchnia całkowita sześcianu wynosi <InlineMath math={pcLatex} />.
        Oblicz długość krawędzi sześcianu.
      </p>
    );
  }

  // TRÓJKĄT DOWOLNY
  if (variant === "triangle_scalene") {
    if (target === "h") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest trójkąt o bokach{" "}
          <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} />,{" "}
          <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`c = ${cLatex}\\,\\text{cm}`} /> oraz wysokości opuszczonej na bok{" "}
          <InlineMath math="a" /> równej{" "}
          <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
          <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
        </p>
      );
    }

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt o bokach{" "}
        <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`c = ${cLatex}\\,\\text{cm}`} /> oraz wysokości opuszczonej na bok{" "}
        <InlineMath math="a" /> równej{" "}
        <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz długość boku <InlineMath math="a" /> w podstawie.
      </p>
    );
  }

  // TRÓJKĄT PROSTOKĄTNY
  if (variant === "triangle_right") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt prostokątny o przyprostokątnych{" "}
        <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
      </p>
    );
  }

  // TRÓJKĄT RÓWNOBOCZNY
  if (variant === "triangle_equilateral") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt równoboczny o boku{" "}
        <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
      </p>
    );
  }

  // KWADRAT
  if (variant === "square") {
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest kwadrat o boku{" "}
        <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
      </p>
    );
  }

  // PROSTOKĄT
  if (variant === "rectangle") {
    if (target === "h") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest prostokąt o bokach{" "}
          <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
          <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
        </p>
      );
    }

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest prostokąt o boku{" "}
        <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} />. Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz długość boku <InlineMath math="a" /> w podstawie.
      </p>
    );
  }

  // RÓWNOLEGŁOBOK
  if (variant === "parallelogram") {
    if (target === "h") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest równoległobok o bokach{" "}
          <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} /> oraz wysokości opuszczonej na bok{" "}
          <InlineMath math="a" /> równej{" "}
          <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
          <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
        </p>
      );
    }

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest równoległobok o boku{" "}
        <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} /> oraz wysokości opuszczonej na bok{" "}
        <InlineMath math="a" /> równej{" "}
        <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz długość boku <InlineMath math="a" /> w podstawie.
      </p>
    );
  }

  // ROMB
  if (variant === "rhombus") {
    if (target === "h") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest romb o boku{" "}
          <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} /> oraz wysokości opuszczonej na ten bok{" "}
          <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
          <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
        </p>
      );
    }

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest romb o wysokości opuszczonej na bok{" "}
        <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz długość boku <InlineMath math="a" /> rombu w podstawie.
      </p>
    );
  }

  // TRAPEZ
  if (variant === "trapezoid") {
    if (target === "h") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest trapez o podstawach{" "}
          <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} /> oraz ramionach{" "}
          <InlineMath math={`c = ${cLatex}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`d = ${dLatex}\\,\\text{cm}`} />. Wysokość trapezu wynosi{" "}
          <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Powierzchnia całkowita graniastosłupa wynosi{" "}
          <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
        </p>
      );
    }

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trapez o podstawie{" "}
        <InlineMath math={`b = ${bLatex}\\,\\text{cm}`} />, ramionach{" "}
        <InlineMath math={`c = ${cLatex}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`d = ${dLatex}\\,\\text{cm}`} /> oraz wysokości trapezu{" "}
        <InlineMath math={`h_a = ${haLatex}\\,\\text{cm}`} />. Wysokość graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${hLatex}\\,\\text{cm}`} />. Powierzchnia całkowita graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz długość podstawy <InlineMath math="a" /> trapezu.
      </p>
    );
  }

  // SZEŚCIOKĄT FOREMNY
  if (variant === "regular_hexagon") {
    const name = baseName || "sześciokąt foremny";
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest {name} o boku{" "}
        <InlineMath math={`a = ${aLatex}\\,\\text{cm}`} />. Powierzchnia całkowita tego graniastosłupa wynosi{" "}
        <InlineMath math={pcLatex} />. Oblicz wysokość graniastosłupa.
      </p>
    );
  }

  // AWARYJNIE
  return (
    <p>
      Dany jest graniastosłup prosty o powierzchni całkowitej{" "}
      <InlineMath math={pcLatex} />. Oblicz brakującą długość wskazaną w
      poleceniu.
    </p>
  );
}

// --- INPUT ---

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const symbol = task.targetSymbol || task.target || "x";

  const raw = extractRawValue(userAnswer);

  const handleChange = (v) => {
    const rawStr = extractRawValue(v);
    if (typeof onChange === "function") onChange({ raw: rawStr, label: symbol });
  };

  return (
    <div className="flex items-center gap-2">
      <InlineMath math={`${symbol} =`} />
      <SqrtInput
        value={raw || ""}
        onChange={handleChange}
        disabled={disabled}
        label={symbol}
        suffixLatex="\text{cm}"
      />
    </div>
  );
}

// --- ODP. UCZNIA ---

export function renderUserAnswer(taskOrWrapper, userAnswerMaybe) {
  // Kompatybilność: czasem wołane jako renderUserAnswer(userAnswer)
  const hasTask = userAnswerMaybe !== undefined;
  const task = hasTask ? unwrapTask(taskOrWrapper) : null;
  const userAnswer = hasTask ? userAnswerMaybe : taskOrWrapper;

  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const raw = extractRawValue(userAnswer);
  const text = String(raw).trim();

  if (!text) return <i>(brak odpowiedzi)</i>;

  const symbol =
    task?.targetSymbol ||
    task?.target ||
    userAnswer?.label ||
    userAnswer?.symbol ||
    userAnswer?.targetSymbol ||
    userAnswer?.target ||
    "x";

  return <InlineMath math={`${symbol} = ${text}\\,\\text{cm}`} />;
}

// --- SPRAWDZANIE ---

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return false;

  const { target, a, h } = task;

  if (!userAnswer) return false;

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw || String(raw).trim() === "") return false;

  const val = typeof userAnswer === "number" ? userAnswer : evaluateExpression(raw);
  if (typeof val !== "number" || !isFinite(val)) return false;

  let correct;
  if (target === "h") correct = h;
  else if (target === "a") correct = a;
  else return false;

  return Math.abs(val - correct) < 1e-2;
}

// --- POPRAWNA ODPOWIEDŹ ---

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const { target, targetSymbol, a, h, aDisplay, hDisplay } = task;
  const symbol = targetSymbol || target || "x";

  let display;
  if (target === "h") display = hDisplay || String(h);
  else if (target === "a") display = aDisplay || String(a);
  else return null;

  return <InlineMath math={`${symbol} = ${display}\\,\\text{cm}`} />;
}

// --- WYJAŚNIENIE ---

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
    variant,
    isCube,
    a,
    b,
    c,
    d,
    baseHeight,
    target,
    aDisplay,
    bDisplay,
    cDisplay,
    dDisplay,
    baseHeightDisplay,
    h,
    hDisplay,
    totalAreaExpr,
    totalAreaValue,
  } = task;

  const aExpr = aDisplay || String(a);
  const bExpr = bDisplay || String(b);
  const cExpr = cDisplay || String(c);
  const dExpr = dDisplay || String(d);
  const haExpr = baseHeightDisplay || String(baseHeight);
  const hExpr = hDisplay || String(h);
  const pcExpr = totalAreaExpr || String(totalAreaValue);

  // --- SZEŚCIAN ---
  if (isCube || variant === "cube") {
    const a2 = a * a;
    const pc = 6 * a2;

    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Zapisujemy wzór na powierzchnię całkowitą sześcianu.
        </p>
        <BlockMath math="P_c = 6a^2" />

        <p>
          <b>Krok 2:</b> Podstawiamy dane.
        </p>
        <BlockMath math={`6a^2 = ${pcExpr}`} />

        <p>
          <b>Krok 3:</b> Wyznaczamy długość krawędzi.
        </p>
        <BlockMath
          math={tex`
\begin{aligned}
a^2 &= \frac{${pcExpr}}{6} = ${aExpr}^2 \\
a &= ${aExpr}\\,\\text{cm}
\end{aligned}
          `}
        />
      </div>
    );
  }

  // --- PRZYPADKI: SZUKAMY h ---
  if (target === "h") {
    // TRÓJKĄT PROSTOKĄTNY – potrzebny Pitagoras do obwodu
    if (variant === "triangle_right") {
      const Pp = (a * b) / 2;
      const Op = a + b + c;

      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Obliczamy przeciwprostokątną trójkąta w podstawie.
          </p>
          <BlockMath math="c^2 = a^2 + b^2" />
          <BlockMath
            math={`c^2 = ${aExpr}^2 + ${bExpr}^2 = ${a * a} + ${b * b} = ${c * c}`} 
          />
          <BlockMath math={`c = ${cExpr}\\,\\text{cm}`} />

          <p>
            <b>Krok 2:</b> Obliczamy pole podstawy.
          </p>
          <BlockMath math={tex`P_p = \dfrac{1}{2}ab`} />
          <BlockMath
            math={`P_p = \\dfrac{1}{2}\\cdot ${aExpr}\\cdot ${bExpr} = ${Pp}\\,\\text{cm}^2`}
          />

          <p>
            <b>Krok 3:</b> Obliczamy obwód podstawy.
          </p>
          <BlockMath math="O_p = a + b + c" />
          <BlockMath math={`O_p = ${aExpr} + ${bExpr} + ${cExpr} = ${Op}\\,\\text{cm}`} />

          <p>
            <b>Krok 4:</b> Korzystamy ze wzoru na powierzchnię całkowitą:
          </p>
          <BlockMath math="P_c = 2P_p + P_b" />
          <BlockMath math={"P_b = O_p\\cdot h"} />
          <BlockMath
            math={tex`
\begin{aligned}
h &= \frac{P_c - 2P_p}{O_p} \\
&= \frac{${pcExpr} - 2\cdot ${Pp}}{${Op}} \\
&= ${hExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    // TRÓJKĄT RÓWNOBOCZNY i SZEŚCIOKĄT FOREMNY – trzymamy pierwiastki "dokładnie"
    if (variant === "triangle_equilateral") {
      const n = task.equiN; // a = 2n
      const PpCoeff = n * n; // Pp = n^2 * sqrt(3)
      const Op = 3 * a;
      const PcCoeff = task.totalCoeff;
      const PbCoeff = task.lateralCoeff;

      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Obliczamy pole podstawy – trójkąta równobocznego.
          </p>
          <BlockMath math={tex`P_p = \dfrac{a^2\sqrt{3}}{4}`} />
          <BlockMath
            math={`P_p = \\dfrac{${aExpr}^2\\sqrt{3}}{4} = ${PpCoeff}\\sqrt{3}\\,\\text{cm}^2`}
          />

          <p>
            <b>Krok 2:</b> Obliczamy obwód podstawy.
          </p>
          <BlockMath math="O_p = 3a" />
          <BlockMath math={`O_p = 3\\cdot ${aExpr} = ${Op}\\,\\text{cm}`} />

          <p>
            <b>Krok 3:</b> Korzystamy ze wzoru <InlineMath math="P_c = 2P_p + P_b" /> oraz <InlineMath math={"P_b = O_p\\cdot h"} />.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
P_c &= ${PcCoeff}\sqrt{3} \\
P_b &= P_c - 2P_p = ${PcCoeff}\sqrt{3} - 2\cdot ${PpCoeff}\sqrt{3} = ${PbCoeff}\sqrt{3} \\
h &= \frac{P_b}{O_p} = \frac{${PbCoeff}\sqrt{3}}{${Op}} = ${hExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    if (variant === "regular_hexagon") {
      const n = task.hexN; // a = 2n
      const PpCoeff = 6 * n * n; // Pp = 6n^2 * sqrt(3)
      const Op = 6 * a;
      const PcCoeff = task.totalCoeff;
      const PbCoeff = task.lateralCoeff;

      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Obliczamy pole podstawy – sześciokąta foremnego.
          </p>
          <BlockMath math={tex`P_p = 6\cdot \dfrac{a^2\sqrt{3}}{4}`} />
          <BlockMath
            math={`P_p = 6\\cdot \\dfrac{${aExpr}^2\\sqrt{3}}{4} = ${PpCoeff}\\sqrt{3}\\,\\text{cm}^2`}
          />

          <p>
            <b>Krok 2:</b> Obliczamy obwód podstawy.
          </p>
          <BlockMath math="O_p = 6a" />
          <BlockMath math={`O_p = 6\\cdot ${aExpr} = ${Op}\\,\\text{cm}`} />

          <p>
            <b>Krok 3:</b> Korzystamy ze wzoru na powierzchnię całkowitą.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
P_c &= ${PcCoeff}\sqrt{3} \\
P_b &= P_c - 2P_p = ${PcCoeff}\sqrt{3} - 2\cdot ${PpCoeff}\sqrt{3} = ${PbCoeff}\sqrt{3} \\
h &= \frac{P_b}{O_p} = \frac{${PbCoeff}\sqrt{3}}{${Op}} = ${hExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    // POZOSTAŁE: standardowo (liczbowo)
    // policz Pp i Op na podstawie wariantu (wszystko jest znane)
    let Pp;
    let Op;
    let PpFormula = "P_p";
    let OpFormula = "O_p";
    let PpSubst = "";
    let OpSubst = "";

    if (variant === "triangle_scalene") {
      Pp = (a * baseHeight) / 2;
      Op = a + b + c;
      PpFormula = tex`P_p = \dfrac{1}{2}ah_a`;
      OpFormula = "O_p = a + b + c";
      PpSubst = `P_p = \\dfrac{1}{2}\\cdot ${aExpr}\\cdot ${haExpr} = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = ${aExpr} + ${bExpr} + ${cExpr} = ${Op}\\,\\text{cm}`;
    } else if (variant === "square") {
      Pp = a * a;
      Op = 4 * a;
      PpFormula = "P_p = a^2";
      OpFormula = "O_p = 4a";
      PpSubst = `P_p = ${aExpr}^2 = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = 4\\cdot ${aExpr} = ${Op}\\,\\text{cm}`;
    } else if (variant === "rectangle") {
      Pp = a * b;
      Op = 2 * a + 2 * b;
      PpFormula = tex`P_p = a\cdot b`;
      OpFormula = "O_p = 2a + 2b";
      PpSubst = `P_p = ${aExpr}\\cdot ${bExpr} = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = 2\\cdot ${aExpr} + 2\\cdot ${bExpr} = ${Op}\\,\\text{cm}`;
    } else if (variant === "parallelogram") {
      Pp = a * baseHeight;
      Op = 2 * a + 2 * b;
      PpFormula = tex`P_p = a\cdot h_a`;
      OpFormula = "O_p = 2a + 2b";
      PpSubst = `P_p = ${aExpr}\\cdot ${haExpr} = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = 2\\cdot ${aExpr} + 2\\cdot ${bExpr} = ${Op}\\,\\text{cm}`;
    } else if (variant === "rhombus") {
      Pp = a * baseHeight;
      Op = 4 * a;
      PpFormula = tex`P_p = a\cdot h_a`;
      OpFormula = "O_p = 4a";
      PpSubst = `P_p = ${aExpr}\\cdot ${haExpr} = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = 4\\cdot ${aExpr} = ${Op}\\,\\text{cm}`;
    } else if (variant === "trapezoid") {
      Pp = ((a + b) * baseHeight) / 2;
      Op = a + b + c + d;
      PpFormula = tex`P_p = \dfrac{(a+b)h_a}{2}`;
      OpFormula = "O_p = a + b + c + d";
      PpSubst = `P_p = \\dfrac{(${aExpr}+${bExpr})\\cdot ${haExpr}}{2} = ${Pp}\\,\\text{cm}^2`;
      OpSubst = `O_p = ${aExpr} + ${bExpr} + ${cExpr} + ${dExpr} = ${Op}\\,\\text{cm}`;
    } else {
      // fallback
      Pp = task.baseArea;
      Op = task.perimeter;
      if (typeof Pp === "number" && isFinite(Pp)) {
        PpSubst = `P_p = ${Pp}\\,\\text{cm}^2`;
      }
      if (typeof Op === "number" && isFinite(Op)) {
        OpSubst = `O_p = ${Op}\\,\\text{cm}`;
      }
    }

    
    const pcVal = typeof totalAreaValue === "number" ? totalAreaValue : NaN;
    const twoPpVal = typeof Pp === "number" && isFinite(Pp) ? 2 * Pp : NaN;
    const numeratorVal = isFinite(pcVal) && isFinite(twoPpVal) ? pcVal - twoPpVal : NaN;

    let solveTex = tex`\begin{aligned}
P_c &= 2P_p + P_b \\
P_b &= O_p\cdot h \\
P_c &= 2P_p + O_p\cdot h \\
O_p\cdot h &= P_c - 2P_p \\
h &= \frac{P_c - 2P_p}{O_p} \\
&= \frac{${pcExpr} - 2\cdot ${Pp}}{${Op}} \\
`;
    if (isFinite(pcVal) && isFinite(twoPpVal) && isFinite(numeratorVal)) {
      solveTex += tex`&= \frac{${pcVal} - ${twoPpVal}}{${Op}} \\
&= \frac{${numeratorVal}}{${Op}} \\
`;
    }
    solveTex += tex`&= ${hExpr}\,\text{cm}
\end{aligned}`;


    return (
      <div className="space-y-4">
        <p>
          <b>Krok 1:</b> Obliczamy pole podstawy.
        </p>
        <BlockMath math={PpFormula} />
        {PpSubst ? <BlockMath math={PpSubst} /> : null}

        <p>
          <b>Krok 2:</b> Obliczamy obwód podstawy.
        </p>
        <BlockMath math={OpFormula} />
        {OpSubst ? <BlockMath math={OpSubst} /> : null}

        
        <p>
          <b>Krok 3:</b> Rozpisujemy wzory, przekształcamy i podstawiamy dane.
        </p>
        <BlockMath math={solveTex} />
      </div>
    );
  }

  // --- PRZYPADKI: SZUKAMY a (równanie liniowe) ---
  if (target === "a") {
    if (variant === "triangle_scalene") {
      // Pc = 2*(1/2 a h_a) + (a+b+c)h = a(h_a + h) + (b+c)h
      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Zapisujemy wzory:
          </p>
          <BlockMath math={tex`P_p = \dfrac{1}{2}ah_a`} />
          <BlockMath math="O_p = a + b + c" />
          <BlockMath math={"P_c = 2P_p + O_p\\cdot h"} />

          <p>
            <b>Krok 2:</b> Podstawiamy dane i rozwiązujemy równanie.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
${pcExpr} &= 2\cdot \frac{1}{2}a\cdot ${haExpr} + (a+${bExpr}+${cExpr})\cdot ${hExpr} \\
${pcExpr} &= a(${haExpr}+${hExpr}) + (${bExpr}+${cExpr})\cdot ${hExpr} \\
a &= \frac{${pcExpr} - (${bExpr}+${cExpr})\cdot ${hExpr}}{${haExpr}+${hExpr}} = ${aExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    if (variant === "rectangle") {
      // Pc = 2ab + (2a+2b)h = a(2b+2h) + 2bh
      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Zapisujemy wzory:
          </p>
          <BlockMath math={tex`P_p = a\cdot b`} />
          <BlockMath math="O_p = 2a + 2b" />
          <BlockMath math={"P_c = 2P_p + O_p\\cdot h"} />

          <p>
            <b>Krok 2:</b> Podstawiamy dane i rozwiązujemy równanie.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
${pcExpr} &= 2\cdot a\cdot ${bExpr} + (2a+2\cdot ${bExpr})\cdot ${hExpr} \\
${pcExpr} &= a(2\cdot ${bExpr} + 2\cdot ${hExpr}) + 2\cdot ${bExpr}\cdot ${hExpr} \\
a &= \frac{${pcExpr} - 2\cdot ${bExpr}\cdot ${hExpr}}{2(${bExpr}+${hExpr})} = ${aExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    if (variant === "parallelogram") {
      // Pc = 2a h_a + (2a+2b)h = 2a(h_a+h) + 2bh
      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Zapisujemy wzory:
          </p>
          <BlockMath math={tex`P_p = a\cdot h_a`} />
          <BlockMath math="O_p = 2a + 2b" />
          <BlockMath math={"P_c = 2P_p + O_p\\cdot h"} />

          <p>
            <b>Krok 2:</b> Podstawiamy dane i rozwiązujemy równanie.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
${pcExpr} &= 2\cdot a\cdot ${haExpr} + (2a+2\cdot ${bExpr})\cdot ${hExpr} \\
${pcExpr} &= 2a(${haExpr}+${hExpr}) + 2\cdot ${bExpr}\cdot ${hExpr} \\
a &= \frac{${pcExpr} - 2\cdot ${bExpr}\cdot ${hExpr}}{2(${haExpr}+${hExpr})} = ${aExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    if (variant === "rhombus") {
      // Pc = 2a h_a + 4a h = a(2h_a + 4h)
      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Zapisujemy wzory:
          </p>
          <BlockMath math={tex`P_p = a\cdot h_a`} />
          <BlockMath math="O_p = 4a" />
          <BlockMath math={"P_c = 2P_p + O_p\\cdot h"} />

          <p>
            <b>Krok 2:</b> Podstawiamy dane i rozwiązujemy równanie.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
${pcExpr} &= 2\cdot a\cdot ${haExpr} + 4a\cdot ${hExpr} \\
${pcExpr} &= a(2\cdot ${haExpr} + 4\cdot ${hExpr}) \\
a &= \frac{${pcExpr}}{2\cdot ${haExpr} + 4\cdot ${hExpr}} = ${aExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }

    if (variant === "trapezoid") {
      // Pc = (a+b)h_a + (a+b+c+d)h = (a+b)(h_a+h) + (c+d)h
      return (
        <div className="space-y-4">
          <p>
            <b>Krok 1:</b> Zapisujemy wzory:
          </p>
          <BlockMath math={tex`P_p = \dfrac{(a+b)h_a}{2}`} />
          <BlockMath math="O_p = a + b + c + d" />
          <BlockMath math={"P_c = 2P_p + O_p\\cdot h"} />

          <p>
            <b>Krok 2:</b> Podstawiamy dane i rozwiązujemy równanie.
          </p>
          <BlockMath
            math={tex`
\begin{aligned}
${pcExpr} &= (a+${bExpr})\cdot ${haExpr} + (a+${bExpr}+${cExpr}+${dExpr})\cdot ${hExpr} \\
${pcExpr} &= (a+${bExpr})(${haExpr}+${hExpr}) + (${cExpr}+${dExpr})\cdot ${hExpr} \\
a &= \frac{${pcExpr} - ${bExpr}(${haExpr}+${hExpr}) - (${cExpr}+${dExpr})\cdot ${hExpr}}{${haExpr}+${hExpr}} = ${aExpr}\\,\\text{cm}
\end{aligned}
            `}
          />
        </div>
      );
    }
  }

  // fallback
  return (
    <div className="space-y-2">
      <p>
        Korzystamy ze wzoru <InlineMath math="P_c = 2P_p + P_b" />, gdzie{" "}
        <InlineMath math={"P_b = O_p\\cdot h"} />, i wyznaczamy szukaną wartość.
      </p>
    </div>
  );
}
